import { Chip, type ChipProps, chipClasses, styled, useThemeProps } from '@mui/material';
import type { ReactNode } from 'react';

import { LabelIcon, TagStateIcon } from '../../../icons';
import { RESOURCE_COLOR_NAMES, type ResourceColorName } from '../../../theme/tokens/resourcesColors';
import type { StateColorName } from '../../../theme/tokens/stateColors';
import { STATE_COLOR_NAMES } from '../../../theme/tokens/stateColors';

export const TAG_COLORS = [
  ...STATE_COLOR_NAMES,
  'ai',
  ...RESOURCE_COLOR_NAMES,
] as const;

export type TagColor = StateColorName | 'ai' | ResourceColorName;

export type TagIconVariant = 'custom' | 'state' | 'label';

const COMPONENT_NAME = 'MuiTag';
const CHIP_SIZE = 'small';
const BORDER_WIDTH = '1px';
const TRANSPARENT = 'transparent';

type Props = {
  label: ReactNode;
  color: TagColor;
  /**
   * Icon variant for the Tag.
   * - `'state'`: Renders the standardized 10px circular state bullet (`<TagStateIcon />`).
   *   Must always be used when the Tag represents an operational or workflow state (e.g. pending, done, cancelled).
   * - `'custom'`: Default. Renders the icon passed to the `icon` prop.
   *
   * @default 'custom'
   */
  variantIcon?: TagIconVariant;
  'data-testid'?: string;
  /**
   * Custom icon component (reserved strictly for non-state tags, such as category badges or AI features).
   * Do not pass an icon here to represent an operational state; use `variantIcon="state"` instead.
   */
  icon?: ChipProps['icon'];
};

type TagOwnerState = {
  color: TagColor;
  variantIcon: TagIconVariant;
};

const TagRoot = styled(Chip)<{ ownerState: TagOwnerState }>(
  ({ theme, ownerState }) => {
    const paletteColor = theme.palette[ownerState.color] ?? theme.palette.default ?? theme.palette.primary;
    const lightColor = paletteColor?.light ?? paletteColor?.main ?? '#f5f5f5';
    const mainColor = paletteColor?.main ?? 'inherit';
    const isResource = (RESOURCE_COLOR_NAMES as readonly string[]).includes(ownerState.color);

    return {
      borderRadius: theme.shape.borderRadius,
      backgroundColor: isResource ? lightColor : theme.lighten(lightColor, 0.85),
      color: mainColor,
      border: `${BORDER_WIDTH} solid ${TRANSPARENT}`,
      [`& .${chipClasses.icon}`]: {
        color: 'inherit',
        ...(ownerState.variantIcon === 'state' && {
          fontSize: '10px',
          marginLeft: theme.spacing(1),
          marginRight: `-${theme.spacing(0.5)}`,
        }),
      },
    };
  },
);

/**
 * Tag atom component used for passive, read-only status and category indicators.
 *
 * Rule: When representing an operational/workflow state (e.g. pending, done, cancelled),
 * always pass `variantIcon="state"` instead of overriding or passing a custom `icon`.
 */
export function Tag(inProps: Props) {
  const props = useThemeProps({ props: inProps, name: COMPONENT_NAME });
  const variantIcon = props.variantIcon ?? 'custom';
  const renderedIcon =
    variantIcon === 'state'
      ? <TagStateIcon />
      : variantIcon === 'label'
        ? <LabelIcon />
        : props.icon;

  return (
    <TagRoot
      label={props.label}
      size={CHIP_SIZE}
      data-testid={props['data-testid']}
      ownerState={{ color: props.color, variantIcon }}
      icon={renderedIcon}
    />
  );
}

export type { Props as TagProps };
